import { classes } from "polytype";
import {
  DataModel,
  ListDataModel,
  ListQueryModel,
  ReferenceModel,
} from "../../base/index.js";
import type {
  CronjobCommandDestination,
  CronjobCreateRequestData,
  CronjobData,
  CronjobListItemData,
  CronjobListQueryData,
  CronjobUpdateAppIdRequestData,
  CronjobUpdateRequestData,
} from "./types.js";
import {
  additionalCronInterpreters,
  defaultCronInterpreters,
} from "./types.js";
import { type AsyncResourceVariant, provideReact } from "../../react/index.js";
import { config } from "../../config/config.js";
import assertObjectFound from "../../base/assertObjectFound.js";
import { Project } from "../../project/index.js";
import { AppInstallation } from "../../app/index.js";
import type { CronjobExecutionListQuery } from "../CronjobExecution/index.js";
import {
  CronjobExecution,
  CronjobExecutionDetailed,
} from "../CronjobExecution/index.js";
import invariant from "invariant";

export class Cronjob extends ReferenceModel {
  public static readonly minTimeout = 1;
  public static readonly maxTimeout = 86400;
  public static readonly defaultTimeout = 3600;
  public readonly executions: CronjobExecutionListQuery;

  public constructor(id: string) {
    super(id);
    this.executions = CronjobExecution.query(this);
  }

  public static ofId(id: string) {
    return new Cronjob(id);
  }

  public static find = provideReact(async (id: string) => {
    const data = await config.behaviors.cronjob.find(id);
    if (data !== undefined) {
      return new CronjobDetailed(data);
    }
  });

  public static get = provideReact(async (id: string) => {
    const cronjob = await this.find(id);
    assertObjectFound(cronjob, "cronjob", id);
    return cronjob;
  });

  public findDetailed = provideReact(
    () => Cronjob.find(this.id),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<CronjobDetailed | undefined>>;

  public getDetailed = provideReact(
    () => Cronjob.get(this.id),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<CronjobDetailed>>;

  public static query(project: Project, query: CronjobListQueryData = {}) {
    return new CronjobListQuery(project, query);
  }

  public static async create(project: Project, data: CronjobCreateRequestData) {
    const result = await config.behaviors.cronjob.create(project.id, data);

    if ("id" in result) {
      return new Cronjob(result.id);
    }

    return result;
  }

  public async update(data: CronjobUpdateRequestData) {
    return await config.behaviors.cronjob.update(this.id, data);
  }

  public async updateAppId(data: CronjobUpdateAppIdRequestData) {
    await config.behaviors.cronjob.updateAppId(this.id, data);
  }

  public async delete() {
    await config.behaviors.cronjob.delete(this.id);
  }

  public async trigger() {
    await config.behaviors.cronjob.trigger(this.id);
  }

  public static getInterpreters = provideReact(
    async (appInstallationId: string) => {
      const systemSoftwares = await AppInstallation.ofId(
        appInstallationId,
      ).getInstalledSystemSoftware({ tagFilter: "interpreter" });

      return [
        ...defaultCronInterpreters,
        ...additionalCronInterpreters.filter((i) =>
          systemSoftwares.find((s) => i.name.toLowerCase() === s.name),
        ),
      ];
    },
  );
}

class CronjobCommon extends classes(
  DataModel<CronjobListItemData | CronjobData>,
  Cronjob,
) {
  public readonly description: string;
  public readonly active: boolean;
  public readonly command?: CronjobCommandDestination;
  public readonly url?: string;
  public readonly type: "command" | "url";
  public readonly interval: string;
  public readonly linkedAppInstallation: AppInstallation;
  public readonly timeout: number;
  public readonly email?: string;
  public readonly latestExecution?: CronjobExecutionDetailed;
  public readonly project: Project;

  public constructor(data: CronjobListItemData | CronjobData) {
    super([data], [data.id]);
    this.description = data.description;
    this.active = data.active;
    this.type = "path" in data.destination ? "command" : "url";
    this.url = "url" in data.destination ? data.destination.url : undefined;
    this.command =
      "path" in data.destination
        ? {
            path: data.destination.path,
            interpreter: data.destination.interpreter,
            parameters: data.destination.parameters,
          }
        : undefined;
    this.interval = data.interval;
    this.linkedAppInstallation = AppInstallation.ofId(data.appId);
    this.timeout = data.timeout;
    this.email = data.email;
    this.latestExecution = data.latestExecution
      ? new CronjobExecutionDetailed(data.latestExecution, this)
      : undefined;
    invariant(data.projectId, `project missing in cronjob ${data.id}`);
    this.project = Project.ofId(data.projectId);
  }
}

export class CronjobDetailed extends classes(
  CronjobCommon,
  DataModel<CronjobData>,
) {
  public constructor(data: CronjobData) {
    super([data], [data]);
  }
}

export class CronjobListItem extends classes(
  CronjobCommon,
  DataModel<CronjobListItemData>,
) {
  public constructor(data: CronjobListItemData) {
    super([data], [data]);
  }
}

export class CronjobListQuery extends ListQueryModel<CronjobListQueryData> {
  public readonly project: Project;

  public constructor(project: Project, query: CronjobListQueryData = {}) {
    super(query, {
      dependencies: [project.id],
    });
    this.project = project;
  }

  public refine(query: CronjobListQueryData) {
    return new CronjobListQuery(this.project, {
      ...this.query,
      ...query,
    });
  }

  public execute = provideReact(async () => {
    const { items } = await config.behaviors.cronjob.list(
      this.project.id,
      this.query,
    );
    return new CronjobList(
      this.project,
      this.query,
      items.map((d) => new CronjobListItem(d)),
    );
  }, [this.queryId, "cronjob"]);
}

export class CronjobList extends classes(
  CronjobListQuery,
  ListDataModel<CronjobListItem>,
) {
  public constructor(
    project: Project,
    query: CronjobListQueryData,
    cronjobs: CronjobListItem[],
  ) {
    super([project, query], [cronjobs, cronjobs.length]);
  }
}
